import React, { useEffect, useMemo, useState } from "react";
import { Eye, Pencil, Plus, Search, X } from "lucide-react";
import api from "../../../api/axios";
import { useToast } from "../../../context/ToastContext";
import { ErrorMessage, validate } from "../../../components/validation/Validation";

const rules = {
  user_name: {
    required: true,
    minLength: 3,
    label: "User Name",
  },
  org_name: {
    required: true,
    label: "Organization Name",
  },
  email: {
    required: true,
    type: "email",
    label: "Email",
  },
  password: {
    required: true,
    minLength: 8,
    label: "Password",
  },
  mobile: {
    required: true,
    pattern: /^[0-9]{10}$/,
    label: "Mobile Number",
    message: "Mobile number must be exactly 10 digits.",
  },
  sub_plan: {
    required: true,
    label: "Subscription Plan",
  },
};

export default function OrganizationsPage() {
  const [organizations, setOrganizations] = useState([]);
  const [search, setSearch] = useState("");
  const [plan, setPlan] = useState("");
  const [form, setForm] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editOrganization, setEditOrganization] = useState(null);
  const [errors, setErrors] = useState({});

  const { success, error } = useToast();

  useEffect(() => {
    loadOrganizations();
  }, []);

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      const response = await api.get("/user/list?role=organization");
      if (response && response.data && response.data.success) {
        setOrganizations(response.data?.data?.list || []);
      } else {
        setLoading(false);
        setOrganizations([]);
      }
    } catch (err) {
      setLoading(false);
      console.log(err?.data?.message);
    }
  };

  const rows = useMemo(
    () =>
      organizations.filter(
        (item) =>
          (item.user_name || "")
            .toLowerCase()
            .includes(search.toLowerCase()) &&
          (!plan || item.subscription?.plan === plan)
      ),
    [organizations, search, plan]
  );

  const saveOrganization = async (event) => {
    event.preventDefault();

    try {
      setLoading(true);

      const payload = Object.fromEntries(
        new FormData(event.currentTarget)
      );

      const validationErrors = validate(payload, rules);

      setErrors(validationErrors);

      if (Object.keys(validationErrors).length > 0) {
        return;
      }


      const response = await api.post("/user/save", payload);
      if (response && response.data && response.data.success) {
        setForm(null);
        loadOrganizations();
        success("Organization created successfully.");
      } else {
        setLoading(false);
        error(response.data?.message || "Failed to save organization.");
      }
    } catch (err) {
      setLoading(false);
      error(err?.data?.message);
    }
  };

  const handleValidationFormChange = (e) => {
    const { name, value } = e.target;

    setErrors((prev) => {
      if (!prev[name]) return prev;

      const newErrors = { ...prev };
      delete newErrors[name];
      return newErrors;
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <h2 className="text-2xl font-bold">Organizations</h2>
          <p className="mt-1 text-sm text-slate-500">
            Manage customer organizations and initial subscriptions.
          </p>
        </div>
        <button
          onClick={() => setForm({})}
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white"
        >
          <Plus size={17} />
          Create organization
        </button>
      </div>
      <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex gap-3 border-b border-slate-100 p-4">
          <label className="relative flex-1">
            <Search
              size={17}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search organizations…"
              className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm"
            />
          </label>
          <select
            value={plan}
            onChange={(e) => setPlan(e.target.value)}
            className="rounded-lg border border-slate-200 px-3 text-sm"
          >
            <option value="">All Plans</option>
            <option value="FREE">FREE</option>
            <option value="STARTER">STARTER</option>
            <option value="PRO">PRO</option>
            <option value="ENTERPRISE">ENTERPRISE</option>
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px] text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                {[
                  "Organization",
                  "Plan",
                  "Users",
                  "Status",
                  "Subscription",
                  "Created",
                  "Actions",
                ].map((x) => (
                  <th key={x} className="px-5 py-3">
                    {x}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((item) => (
                <tr key={item.id} className="hover:bg-slate-50">

                  {/* Organization */}
                  <td className="px-5 py-4">
                    <div className="font-semibold">
                      {item.user_name || "-"}
                    </div>

                    <div className="text-xs text-slate-500">
                      {item.email}
                    </div>
                  </td>

                  {/* Plan */}
                  <td className="px-5 py-4">
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${{
                        FREE: "bg-green-100 text-green-700",
                        STARTER: "bg-blue-100 text-blue-700",
                        PRO: "bg-violet-100 text-violet-700",
                        ENTERPRISE: "bg-amber-100 text-amber-700",
                      }[item.subscription?.plan] ||
                        "bg-slate-100 text-slate-700"
                        }`}
                    >
                      {item.subscription?.plan || "-"}
                    </span>
                  </td>

                  {/* Users */}
                  <td className="px-5 py-4">
                    {item.subscription?.usage?.users || 0} /
                    {" "}
                    {item.subscription?.limits?.users || 0}
                  </td>

                  {/* Status */}
                  <td className="px-5 py-4">
                    <OrganizationStatus value={item.status} />
                  </td>

                  {/* Subscription */}
                  <td className="px-5 py-4">
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${item.subscription?.status === "ACTIVE"
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-red-100 text-red-700"
                        }`}
                    >
                      {item.subscription?.status}
                    </span>
                  </td>

                  {/* Created */}
                  <td className="px-5 py-4 whitespace-nowrap">
                    {new Date(item.created_on).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}

                    <div className="text-xs text-slate-500">
                      {new Date(item.created_on).toLocaleTimeString("en-IN")}
                    </div>
                  </td>

                  {/* Actions */}
                  <td className="px-5 py-4">
                    <button
                      onClick={() => setSelected(item)}
                      className="rounded-lg p-2 hover:bg-slate-100"
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      onClick={() => setEditOrganization(item)}
                      className="rounded-lg p-2 hover:bg-indigo-100 text-indigo-600"
                      title="Edit"
                    >
                      <Pencil size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      {form && (
        <OrganizationForm
          onClose={() => setForm(null)}
          onSave={saveOrganization}
          errors={errors}
          handleFormChange={handleValidationFormChange}
        />
      )}
      {editOrganization && (
        <OrganizationEdit
          value={editOrganization}
          onClose={() => setEditOrganization(null)}
          onUpdate={() => {
            setEditOrganization(null);
            loadOrganizations();
            // Refresh the organization list or update the specific organization
          }}
        />
      )}
      {selected && (
        <OrganizationDetails
          value={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

const OrganizationStatus = ({ value }) => (
  <span
    className={`rounded-full px-3 py-1 text-xs font-semibold ${value === "ACTIVE"
      ? "bg-emerald-100 text-emerald-700"
      : value === "SUSPENDED"
        ? "bg-yellow-100 text-yellow-700"
        : "bg-red-100 text-red-700"
      }`}
  >
    {value}
  </span>
);

function OrganizationForm({ onClose, onSave, errors, handleFormChange }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4 !m-0 !h-full">
      <form
        onSubmit={onSave}
        className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <h3 className="text-xl font-semibold">Create Organization</h3>
            <p className="mt-1 text-sm text-slate-500">
              Create a new organization and assign a subscription plan.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 hover:bg-slate-100"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="grid gap-5 p-6 sm:grid-cols-2">

          {/* User Name */}
          <div>
            <label className="mb-2 block text-sm font-medium">
              User Name <span className="text-red-500">*</span>
            </label>
            <input
              name="user_name"
              id="user_name"
              placeholder="Enter user name"
              onChange={handleFormChange}
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:border-indigo-500 focus:outline-none"
            />
            <ErrorMessage error={errors.user_name} />
          </div>

          {/* Organization */}
          <div>
            <label className="mb-2 block text-sm font-medium">
              Organization Name <span className="text-red-500">*</span>
            </label>
            <input
              name="org_name"
              placeholder="Enter organization"
              onChange={handleFormChange}
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:border-indigo-500 focus:outline-none"
            />
            <ErrorMessage error={errors.org_name} />
          </div>

          {/* Email */}
          <div>
            <label className="mb-2 block text-sm font-medium">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              placeholder="Enter email"
              onChange={handleFormChange}
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:border-indigo-500 focus:outline-none"
            />
            <ErrorMessage error={errors.email} />
          </div>

          {/* Password */}
          <div>
            <label className="mb-2 block text-sm font-medium">
              Password <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              name="password"
              placeholder="Enter password"
              onChange={handleFormChange}
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:border-indigo-500 focus:outline-none"
            />
            <ErrorMessage error={errors.password} />
          </div>

          {/* Mobile */}
          <div>
            <label className="mb-2 block text-sm font-medium">
              Mobile <span className="text-red-500">*</span>
            </label>
            <input
              name="mobile"
              maxLength={10}
              placeholder="9999999999"
              onChange={handleFormChange}
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:border-indigo-500 focus:outline-none"
            />
            <ErrorMessage error={errors.mobile} />
          </div>

          {/* Subscription Plan */}
          <div>
            <label className="mb-2 block text-sm font-medium">
              Subscription Plan <span className="text-red-500">*</span>
            </label>

            <select
              name="sub_plan"
              defaultValue="FREE"
              onChange={handleFormChange}
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:border-indigo-500 focus:outline-none"
            >
              <option value="FREE">FREE</option>
              <option value="STARTER">STARTER</option>
              <option value="PRO">PRO</option>
              <option value="ENTERPRISE">ENTERPRISE</option>
            </select>
            <ErrorMessage error={errors.sub_plan} />
          </div>

        </div>

        {/* Hidden Role */}
        <input
          type="hidden"
          name="role"
          value="organization"
        />

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-5 py-2.5 text-sm font-medium hover:bg-slate-50"
          >
            Cancel
          </button>

          <button
            type="submit"
            className="rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700"
          >
            Create Organization
          </button>
        </div>
      </form>
    </div>
  );
}

function OrganizationEdit({ value, onClose, onUpdate }) {
  const [form, setForm] = useState({
    user_name: value?.user_name || "",
    mobile: value?.mobile || "",
    status: value?.status || "ACTIVE",
  });

  const { success, error } = useToast();

  const handleUpdate = async (e) => {
    e.preventDefault();

    try {
      await api.put(`/user/${value.id}`, form);
      success("Organization updated successfully.");
      onUpdate?.();
      onClose();
    } catch (err) {
      error(err?.response?.data?.message || "Failed to update organization.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4 !m-0 !h-full">
      <form
        onSubmit={handleUpdate}
        className="w-full max-w-xl rounded-2xl bg-white shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <h3 className="text-xl font-semibold">Edit Organization</h3>
            <p className="mt-1 text-sm text-slate-500">
              Update organization details.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 hover:bg-slate-100"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="grid gap-5 p-6 sm:grid-cols-2">

          {/* User Name */}
          <div>
            <label className="mb-2 block text-sm font-medium">
              User Name
            </label>
            <input
              value={form.user_name}
              onChange={(e) =>
                setForm({ ...form, user_name: e.target.value })
              }
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:border-indigo-500 focus:outline-none"
            />
          </div>

          {/* Email (Readonly) */}
          <div>
            <label className="mb-2 block text-sm font-medium">
              Email
            </label>
            <input
              value={value.email}
              disabled
              className="w-full rounded-lg border border-slate-200 bg-slate-100 px-3 py-2.5 text-slate-500"
            />
          </div>

          {/* Mobile */}
          <div>
            <label className="mb-2 block text-sm font-medium">
              Mobile
            </label>
            <input
              value={form.mobile}
              onChange={(e) =>
                setForm({ ...form, mobile: e.target.value })
              }
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:border-indigo-500 focus:outline-none"
            />
          </div>

          {/* Status */}
          <div>
            <label className="mb-2 block text-sm font-medium">
              Status
            </label>

            <select
              value={form.status}
              onChange={(e) =>
                setForm({ ...form, status: e.target.value })
              }
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:border-indigo-500 focus:outline-none"
            >
              <option value="ACTIVE">ACTIVE</option>
              <option value="INACTIVE">INACTIVE</option>
              <option value="SUSPENDED">SUSPENDED</option>
            </select>
          </div>

        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-5 py-2.5 text-sm font-medium hover:bg-slate-50"
          >
            Cancel
          </button>

          <button
            type="submit"
            className="rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700"
          >
            Update Organization
          </button>
        </div>
      </form>
    </div>
  );
}

function OrganizationDetails({ value, onClose }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/40 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex justify-between">
          <div>
            <h3 className="text-xl font-bold">
              {value.user_name || "-"}
            </h3>

            <p className="text-sm text-slate-500">
              {value.email}
            </p>
          </div>
          <button onClick={onClose}>
            <X size={18} />
          </button>
        </div>
        <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-5">
          <h4 className="mb-4 text-base font-semibold text-slate-800">
            Subscription Usage
          </h4>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">

            <div className="rounded-lg bg-white p-3 shadow-sm">
              <p className="text-xs text-slate-500">Users</p>
              <p className="mt-1 text-lg font-semibold text-slate-900">
                {value.subscription?.usage?.users ?? 0}
                <span className="text-sm font-normal text-slate-500">
                  {" / "}
                  {value.subscription?.limits?.users ?? 0}
                </span>
              </p>
            </div>

            <div className="rounded-lg bg-white p-3 shadow-sm">
              <p className="text-xs text-slate-500">Creatives</p>
              <p className="mt-1 text-lg font-semibold text-slate-900">
                {value.subscription?.usage?.creatives ?? 0}
                <span className="text-sm font-normal text-slate-500">
                  {" / "}
                  {value.subscription?.limits?.creatives ?? 0}
                </span>
              </p>
            </div>

            <div className="rounded-lg bg-white p-3 shadow-sm">
              <p className="text-xs text-slate-500">Campaigns</p>
              <p className="mt-1 text-lg font-semibold text-slate-900">
                {value.subscription?.usage?.campaigns ?? 0}
                <span className="text-sm font-normal text-slate-500">
                  {" / "}
                  {value.subscription?.limits?.campaigns ?? 0}
                </span>
              </p>
            </div>

            <div className="rounded-lg bg-white p-3 shadow-sm">
              <p className="text-xs text-slate-500">Templates</p>
              <p className="mt-1 text-lg font-semibold text-slate-900">
                {value.subscription?.usage?.templates ?? 0}
                <span className="text-sm font-normal text-slate-500">
                  {" / "}
                  {value.subscription?.limits?.templates ?? 0}
                </span>
              </p>
            </div>

            <div className="rounded-lg bg-white p-3 shadow-sm">
              <p className="text-xs text-slate-500">Brands</p>
              <p className="mt-1 text-lg font-semibold text-slate-900">
                {value.subscription?.usage?.brands ?? 0}
                <span className="text-sm font-normal text-slate-500">
                  {" / "}
                  {value.subscription?.limits?.brands ?? 0}
                </span>
              </p>
            </div>

            <div className="rounded-lg bg-white p-3 shadow-sm">
              <p className="text-xs text-slate-500">Plan</p>
              <span
                className={`mt-2 inline-flex rounded-full px-3 py-1 text-xs font-semibold ${{
                  FREE: "bg-green-100 text-green-700",
                  STARTER: "bg-blue-100 text-blue-700",
                  PRO: "bg-violet-100 text-violet-700",
                  ENTERPRISE: "bg-amber-100 text-amber-700",
                }[value.subscription?.plan] || "bg-slate-100 text-slate-700"
                  }`}
              >
                {value.subscription?.plan || "-"}
              </span>
            </div>

          </div>
        </div>
        <button
          onClick={onClose}
          className="mt-6 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
        >
          Close
        </button>
      </div>
    </div>
  );
}
